import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { useAuth } from './AuthContext';
import { MfaEnrolementScreen } from './MfaEnrolementScreen';

/**
 * L'adresse de contact publiée sur le site vitrine. C'est la seule voie d'accès
 * publique et vérifiable : il n'existe aujourd'hui aucun lien de téléchargement
 * public de l'application (build TestFlight, invitation nominative). On ne
 * fabrique donc pas un bouton « Télécharger l'application » qui ne mènerait
 * nulle part — un cul-de-sac déguisé reste un cul-de-sac.
 */
const CONTACT_EMAIL = 'info@neth.ca';
/** Le sujet pré-rempli de la demande d'invitation (LOT 3.5, CON-PAR-LOG-02). */
const SUJET_INVITATION = encodeURIComponent('Silex — demande d’accès à l’application');

function ScreenMessage({
  title,
  children,
  actions,
}: {
  title: string;
  children: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="screen-message">
      <div className="screen-message__inner">
        <h1>{title}</h1>
        {children}
        {actions ? <div className="screen-message__actions">{actions}</div> : null}
      </div>
    </div>
  );
}

/**
 * Garde de route de la console.
 *
 * - `loading` → écran d'attente pendant la reprise de session.
 * - non connecté → redirection vers `/login` (mémorise la cible).
 * - rôle `parent` / `eleve` → **une orientation, pas un mur** (SLX-116).
 * - `adminOnly` → seuls les comptes `admin` passent.
 *
 * SLX-116 — POURQUOI CE N'EST PLUS « ACCÈS NON AUTORISÉ ».
 *
 * Le rôle n'est connu qu'APRÈS l'authentification : le serveur ne le révèle pas
 * avant, et c'est très bien ainsi (répondre « ce compte est un parent » sur un
 * formulaire de connexion est une fuite d'information). Un refus littéralement
 * « à la porte », avant toute saisie, est donc impossible à tenir côté client.
 * Ce qui est possible, et qui est fait, est double :
 *
 *  1. **Ne plus l'inviter.** `WelcomePage` n'offre plus de bloc « Je suis
 *     parent → Se connecter », et le site vitrine n'envoie plus les familles
 *     vers cette console (`apps/site`, vue « Où se passe Silex »). C'est la
 *     porte, et elle est honnête avant la saisie.
 *  2. **Ne jamais laisser un parent authentifié devant un mur.** S'il arrive
 *     ici malgré tout — vieux courriel, marque-page, identifiants saisis
 *     directement sur `/login` — il lit où se trouve son espace et par où
 *     passer, pas un refus sec dont la seule issue est « Se déconnecter ».
 *
 * Le document de conception tranche le fond (EXPERIENCE-PARENT-TUTEUR §4.3 et
 * §4.5) : le parent n'a pas de console web au MVP. Sa surface est
 * l'application, plus le rapport par courriel — le besoin réel derrière « il me
 * faudrait un ordinateur » est un besoin de TRANSMISSION, servi par un document
 * qu'on transfère et qu'on imprime, pas par un quatrième client à maintenir.
 *
 * Registre : vouvoiement pour le parent, tutoiement pour l'élève
 * (`docs/GUIDE-DE-TON-SILEX.md`).
 */
export function RequireAuth({
  children,
  adminOnly = false,
}: {
  children: ReactNode;
  adminOnly?: boolean;
}) {
  const { status, user, logout } = useAuth();
  const location = useLocation();

  if (status === 'loading') {
    return (
      <ScreenMessage title="Chargement…">
        <p>Reprise de votre session en cours.</p>
      </ScreenMessage>
    );
  }

  if (status === 'anonymous' || !user) {
    return (
      <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}` }} />
    );
  }

  // AB#296 — le rôle impose le double facteur et le compte ne l'a pas enrôlé :
  // l'API refuse toutes les routes métier à cette session (403
  // `MFA_ENROLEMENT_REQUIS`). On ne montre donc pas une console qui
  // n'afficherait que des erreurs, mais la seule chose à faire.
  if (user.mfaEnrolementRequis) {
    return <MfaEnrolementScreen />;
  }

  const seDeconnecter = (
    <button type="button" className="btn btn--ghost" onClick={() => void logout()}>
      Se déconnecter
    </button>
  );

  if (user.role === 'parent') {
    return (
      <ScreenMessage
        title="Votre espace se trouve dans l'application Silex"
        actions={seDeconnecter}
      >
        <p>
          Cette console est l'outil des tuteurs. Le suivi de votre enfant est dans l'application
          Silex, sur votre téléphone.
        </p>
        {/* LOT 3.5 (CON-PAR-LOG-02) — « le cas demandait "Votre suivi se
            trouve dans l'application Silex" PLUS UN LIEN DE TÉLÉCHARGEMENT. La
            première moitié y est mot pour mot ; la seconde non. Un parent qui
            n'a pas encore installé l'application est renvoyé vers une
            application, sans lien pour l'obtenir. »

            ⚠️ Le produit n'est PAS publié : il est en distribution TestFlight,
            sur invitation. Poser un lien vers l'App Store serait un lien mort,
            c'est-à-dire pire que pas de lien. On dit donc où en est la
            distribution et comment obtenir l'accès — c'est ce que le parent
            cherche réellement derrière « où est-ce que je la télécharge ». Le
            jour de la publication, cette phrase devient le lien. */}
        <p>
          Vous n'avez pas encore l'application&nbsp;? Elle est pour l'instant distribuée sur
          invitation, pas encore publiée dans les magasins d'applications.{' '}
          <a href={`mailto:${CONTACT_EMAIL}?subject=${SUJET_INVITATION}`}>
            Demandez votre invitation à {CONTACT_EMAIL}
          </a>{' '}
          et nous vous envoyons le lien d'installation.
        </p>
        <p>
          Une question sur votre accès&nbsp;?{' '}
          <a href={`mailto:${CONTACT_EMAIL}`}>Écrivez-nous à {CONTACT_EMAIL}</a>.
        </p>
      </ScreenMessage>
    );
  }

  if (user.role === 'eleve') {
    return (
      <ScreenMessage title="Silex se passe dans l'application" actions={seDeconnecter}>
        <p>Cette console est l'outil des professeurs. Ton espace est dans l'application Silex.</p>
      </ScreenMessage>
    );
  }

  if (user.role !== 'tuteur' && user.role !== 'admin') {
    return (
      <ScreenMessage title="Cette console est réservée aux professeurs" actions={seDeconnecter}>
        <p>
          Votre compte n'ouvre pas d'espace de travail ici.{' '}
          <a href={`mailto:${CONTACT_EMAIL}`}>Écrivez-nous à {CONTACT_EMAIL}</a> et nous vous
          orienterons.
        </p>
      </ScreenMessage>
    );
  }

  if (adminOnly && user.role !== 'admin') {
    return (
      <ScreenMessage title="Section réservée">
        <p>L'administration est accessible aux comptes admin uniquement.</p>
      </ScreenMessage>
    );
  }

  return <>{children}</>;
}
